/**
 * Stateless banner row above the form body — save errors, decline comments,
 * needs-revision notice, pinned-old-version notice, missing-justifications.
 * All purely presentational; the panel still owns the state machine.
 */
import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react'

export interface FormFillBannersProps {
  saveError: string | null
  openDeclineFieldCommentCount: number
  isNeedsRevisionSubmitted: boolean
  revisionReason?: string
  isPinnedOldVersionContinued: boolean
  pinnedVersionLabel: string
  onUpgradeRequest: () => void
  isReadOnly: boolean
  isSubmitted: boolean
  canEdit: boolean
  missingJustificationsCount: number
}

export function FormFillBanners(props: FormFillBannersProps) {
  return (
    <>
      {props.saveError && (
        <div className="mx-6 mt-4 flex items-center gap-2 bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3 text-sm text-red-400">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{props.saveError}</span>
        </div>
      )}

      {props.openDeclineFieldCommentCount > 0 && (
        <div className="mx-6 mt-4 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
          <p className="text-sm text-red-400 font-medium mb-1">
            {props.openDeclineFieldCommentCount.toLocaleString('fa-IR')} نظر اصلاحی روی فیلدهای این فرم وجود دارد
          </p>
          <p className="text-xs text-red-300">نظرات را در زیر هر فیلد مشاهده و پس از اصلاح علامت‌گذاری کنید</p>
        </div>
      )}

      {props.isNeedsRevisionSubmitted && (
        <div className="mx-6 mt-4 flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-4 py-3 text-sm text-yellow-400">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>
            {props.revisionReason === 'form_breaking_change'
              ? 'فرم به‌روزرسانی شده — لطفاً فیلدهای علامت‌گذاری‌شده را بررسی کنید.'
              : 'سابقه رد شده — می‌توانید فرم را ویرایش و مجدداً ارسال کنید.'}
          </span>
        </div>
      )}

      {props.isPinnedOldVersionContinued && (
        <div className="mx-6 mt-4 flex items-center justify-between gap-2 bg-blue-500/10 border border-blue-500/20 rounded-lg px-4 py-2 text-xs text-blue-400">
          <span>این فرم روی نسخه قدیمی (v{props.pinnedVersionLabel}) پر شده.</span>
          <button
            className="underline underline-offset-2 hover:opacity-80"
            onClick={props.onUpgradeRequest}
          >
            ارتقا به نسخه جدید
          </button>
        </div>
      )}

      {props.isReadOnly && props.isSubmitted && (
        <div className="mx-6 mt-4 flex items-center gap-2 bg-muted/30 border border-border rounded-lg px-4 py-3 text-sm text-muted-foreground">
          <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
          <span>این فرم ارسال شده است.{props.canEdit ? '' : ' برای ویرایش با مدیر پروژه تماس بگیرید.'}</span>
        </div>
      )}

      {props.missingJustificationsCount > 0 && (
        <div className="mx-6 mt-4 flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-2 text-xs text-yellow-400">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          <span>
            {props.missingJustificationsCount.toLocaleString('fa-IR')} هشدار اعتبارسنجی نیاز به توجیه بالینی دارد — زیر هر فیلد توضیح دهید
          </span>
        </div>
      )}
    </>
  )
}
